import { type ReactElement, type ReactNode, useId } from 'react';
import { createPortal } from 'react-dom';

import { useSheetDialog } from '@/hooks/use-sheet-dialog';
import { useI18n } from '@/i18n';
import { hapticImpact } from '@/lib/haptic';

import { Button } from './button';

export function Sheet({
  open,
  onClose,
  title,
  children,
  closeDisabled = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  closeDisabled?: boolean;
}): ReactElement | null {
  const { t } = useI18n();
  const titleId = useId();
  const { dialogRef, panelRef } = useSheetDialog(open, onClose, closeDisabled);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 overscroll-none"
      style={{ height: 'min(var(--app-height), var(--tg-viewport-height, 100dvh))' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        tabIndex={-1}
        className="absolute inset-0 bg-[#252536]/45 backdrop-blur-[4px]"
        aria-label={t('common.close')}
        disabled={closeDisabled}
        onClick={() => {
          hapticImpact('light');
          onClose();
        }}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className="studio-sheet animate-fade-up absolute right-0 bottom-0 left-0 max-h-[85%] overflow-y-auto overscroll-contain rounded-t-[var(--radius-lg)] border border-border bg-background p-4 shadow-2xl"
        style={{
          paddingBottom: 'max(1.5rem, var(--safe-area-inset-bottom))',
          paddingLeft: 'max(1rem, var(--safe-area-inset-left))',
          paddingRight: 'max(1rem, var(--safe-area-inset-right))',
        }}
      >
        <div
          className="mx-auto mb-4 h-1 w-10 rounded-full bg-muted/40"
          aria-hidden
        />
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2
            id={titleId}
            className="min-w-0 flex-1 text-base leading-snug font-semibold"
          >
            {title}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={closeDisabled}
          >
            {t('common.close')}
          </Button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
